// src/private/src/router/index.jsx
// 
import React from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';

import { Helmet } from 'react-helmet-async';

import { AuthLayout, AppLayout, ProviderComposer } from '@src/components';
import { I18nProvider } from '@src/components/core/i18n-provider';
import { LocalizationProvider } from '@src/components/core/localization-provider';
import { ScrollRestoration } from '@src/components/core/scroll-restoration';
import { Toaster } from '@src/components/core/toaster';
import config from '@src/config';
import paths from '@src/paths';
import { VerifyContainer } from '@src/containers';
import { AuthProvider, MessageProvider, SidebarProvider } from '@src/context/common';
import { useAuth } from '@src/context';
import {
  Login,
  Page404,
  Page500,
  ErrorBoundary,
  DailyPulse,
  DataBackfillPage,
  PerformanceTrends,
  ClientPerformance,
  CompanyPerformance,
  ClientPerformanceAggregator,
  DashboardOverviewPage,
  BackendDataListPage,
  BackendDataSummaryPage,
  TaskBoardPage,
  ClientInboxPage,
  NotificationsPage,
  KnowledgeBasePage,
  CreativeStudioPage,
  PublicInvoicePage,
  OnboardingFlowPage,
  PublicProjectionsPage,
  PublicProjectionsClientView,
  EmailMappingBuilderPage,
  TaskLoggerPage,
  AccountPage,
  PasswordReset,
  AdNameBuilderPage,
  CampaignBuilderPage,
  BudgetOptimizerPage,
} from '@src/pages';

import { DailyPulseProvider, ClientPerformanceProvider, OverviewProvider  } from '@src/context';
import { ClientListPage, ClientDetailPage, ClientZipsPage, ClientDashboardPage, ClientBudgetsPage, BudgetCenterPage, ClientLanesPage, ClientOffersPage, ClientLandingPagesPage, ClientCreativeKnowledgePage, ClientHubPage, OnboardingListPage, OnboardingWorkflowPage, ClientMemberInvitePage } from '@src/pages/app/clients';
import { InvoiceListPage, InvoiceDetailPage, InvoiceFormPage, InvoiceSettingsPage } from '@src/pages/app/invoices';
import { LeadListPage } from '@src/pages/app/leads';
import LeadDetailPage from '@src/pages/app/leads/LeadDetailPage'; 
import FlowHubPage from '@src/pages/app/flows/FlowHubPage';
import ZipCodeUpdatesFlowPage from '@src/pages/app/flows/ZipCodeUpdatesFlowPage';









import Protect from './Protect';

const PROVIDERS = [
  LocalizationProvider,
  MessageProvider,
  AuthProvider,
  [I18nProvider, { language: 'en' }],
  SidebarProvider,
];

function normalizeSlug(value = '') {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function getMembershipDashboardSlug(membership = {}) {
  const clientSlug = membership?.client_slug || membership?.client_id;
  if (clientSlug) {
    return clientSlug;
  }
  const parentName = membership?.parent_company_name;
  if (parentName) {
    return normalizeSlug(parentName);
  }
  return membership?.parent_company_id || null;
}

function RootContainer() {
  return (
    <ErrorBoundary>
      <Helmet>
        <title>{config.SITE.title}</title>
      </Helmet>
      <ProviderComposer providers={PROVIDERS}>
        <ScrollRestoration/>
        <VerifyContainer>
          <ClientMemberRouteGuard>
            <Outlet/>
          </ClientMemberRouteGuard>
          <Toaster position="bottom-right"/>
        </VerifyContainer>
      </ProviderComposer>
    </ErrorBoundary>
  );
}

function ClientMemberRouteGuard({ children }) {
  const { state } = useAuth();
  const location = useLocation();
  const user = state?.user;
  const memberships = user?.client_memberships || [];
  const isClientMember = !user?.is_staff && !user?.is_superuser && memberships.length > 0;

  if (!isClientMember) {
    return children;
  }

  const primary = memberships[0] || {};
  const slug = getMembershipDashboardSlug(primary);
  const isParentScopeMembership = !primary?.client_id && Boolean(
    primary?.parent_company_id || primary?.parent_company_name
  );
  const status = String(primary?.client_status || '').toLowerCase();
  const path = location.pathname.replace(/\/+$/, '');
  const onboardingPath = slug ? `/onboarding/${slug}` : null;
  const dashboardPath = slug ? `/clients/${slug}/dashboard` : null;
  const anyClientDashboardPath = /^\/clients\/[^/]+\/dashboard$/.test(path);
  const isLeadPath = path === '/leads' || /^\/leads\/[^/]+$/.test(path);
  const isPublicInvoicePath = /^\/invoices\/[^/]+$/.test(path);
  const accountPath = '/dashboard/settings/account';
  const flowPaths = new Set(['/flows', '/flows/zip-code-updates']);
  const onboardingStatuses = new Set(['new_business', 'onboarding']);

  if (flowPaths.has(path)) {
    return children;
  }

  if (isParentScopeMembership && anyClientDashboardPath) {
    return children;
  }

  if (isLeadPath) {
    return children;
  }

  if (isPublicInvoicePath) {
    return children;
  }

  if (onboardingStatuses.has(status)) {
    if (onboardingPath && path === onboardingPath) {
      return children;
    }
    if (onboardingPath) {
      return <Navigate to={onboardingPath} replace />;
    }
    return <Navigate to={paths.home} replace />;
  }

  if (path === accountPath || (dashboardPath && path === dashboardPath)) {
    return children;
  }

  if (dashboardPath) {
    return <Navigate to={dashboardPath} replace />;
  }
  return <Navigate to={accountPath} replace />;
}

function AppContainer() {
  return (
    <Protect.Authenticated>
      <AppLayout>
        <Outlet/>
      </AppLayout>
    </Protect.Authenticated>
  );
}

function AuthContainer() {
  return (
    <Protect.Any>
      <AuthLayout>
        <Outlet />
      </AuthLayout>
    </Protect.Any>
  );
}

function StaffOnly({ children }) {
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;
  if (isStaff) {
    return children;
  }
  const memberships = user?.client_memberships || [];
  const isClientMember = !user?.is_staff && !user?.is_superuser && memberships.length > 0;
  if (isClientMember) {
    const primary = memberships[0] || {};
    const dashboardSlug = getMembershipDashboardSlug(primary);
    if (dashboardSlug) {
      return <Navigate to={paths.clientDashboard(dashboardSlug)} replace />;
    }
  }
  return <Navigate to={paths.clients} replace />;
}

// https://reactrouter.com/en/main/start/overview
const router = createBrowserRouter([
  {
    element: <RootContainer />,
    children: [
      {
        path: '/',
        element: <AuthContainer />,
        children: [
          {
            path: '',
            element: <Login />,
          },
          {
            path: 'auth/login',
            element: <Login />,
          },
          {
            path: 'auth/cognito/reset-password',
            element: <PasswordReset />,
          },
        ],
      },
      {
        path: 'projections/:clientSlug/client-view',
        element: <Protect.Any><PublicProjectionsClientView /></Protect.Any>,
      },
      {
        path: '/',
        element: <AppContainer />,
        children: [
          {
            path: 'daily-pulse',
            element: (
              <StaffOnly>
                <DailyPulseProvider>
                  <DailyPulse />
                </DailyPulseProvider>
              </StaffOnly>
            ),
          },
          {
            path: 'data-backfill',
            element: (
              <StaffOnly>
                <DataBackfillPage />
              </StaffOnly>
            ),
          },
          {
            path: 'performance-trends',
            element: <PerformanceTrends />,
          },
          {
            path: 'dashboard-overview',
            element: (
              <StaffOnly>
                <OverviewProvider>
                  <DashboardOverviewPage />
                </OverviewProvider>
              </StaffOnly>
            ), // ✅ NEW
          },
          
          {
            path: 'client-performance',
            element: (
              <StaffOnly>
                <ClientPerformanceProvider>
                  <ClientPerformance />
                </ClientPerformanceProvider>
              </StaffOnly>
            ),
          },
          {
            path: 'company-performance',
            element: (
              <StaffOnly>
                <CompanyPerformance />
              </StaffOnly>
            ),
          },
          {
            path: 'creative-performance',
            element: (
              <StaffOnly>
                <ClientPerformanceAggregator />
              </StaffOnly>
            ),
          },
          {
            path: 'client-performance-aggregator',
            element: <Navigate to={paths.dashboard.clientPerformanceAggregator} replace />,
          },

          {
            path: 'clients',
            element: (
              <ErrorBoundary>
                <ClientListPage />
              </ErrorBoundary>
            ),
          },
          {
            path: 'clients/invite-members',
            element: <ClientMemberInvitePage />,
          },
          {
            path: 'clients/new',
            element: <ClientDetailPage />,
          },
          {
            path: 'clients/:clientId/edit',
            element: <ClientHubPage />,
          },
          {
            path: 'clients/:clientId',
            element: <ClientHubPage />,
          },
          {
            path: 'clients/:clientId/hub',
            element: <ClientHubPage />,
          },
          {
            path: 'clients/:clientId/dashboard',
            element: <ClientDashboardPage />,
          },
          {
            path: 'clients/lanes',
            element: <ClientLanesPage />,
          },
          {
            path: 'clients/onboarding',
            element: <OnboardingListPage />,
          },
          {
            path: 'clients/onboarding/:clientSlug/workflow',
            element: <OnboardingWorkflowPage />,
          },
          {
            path: 'clients/client-zips',
            element: <ClientZipsPage />
          },
          {
            path: 'client-budgets',
            element: <ClientBudgetsPage />,
          },
          {
            path: 'budget-center',
            element: <BudgetCenterPage />,
          },
          {
            path: 'client-offers',
            element: <ClientOffersPage />,
          },
          {
            path: 'clients/creative-knowledge',
            element: <ClientCreativeKnowledgePage />,
          },
          {
            path: 'client-landing-pages',
            element: <ClientLandingPagesPage />,
          },
          {
            path: 'clients/client-landing-pages',
            element: <ClientLandingPagesPage />,
          },
          {
            path: 'dashboard/invoices',
            element: <InvoiceListPage />,
          },
          {
            path: 'dashboard/invoices/create',
            element: <InvoiceFormPage />,
          },
          {
            path: 'dashboard/invoices/settings',
            element: <InvoiceSettingsPage />,
          },
          {
            path: 'dashboard/invoices/:invoiceId',
            element: <InvoiceDetailPage />,
          },
          {
            path: 'dashboard/invoices/:invoiceId/edit',
            element: <InvoiceFormPage />,
          },
          {
            path: 'leads',
            element: <LeadListPage />,
          },
          {
            path: 'backend-data',
            element: <BackendDataListPage />,  // ✅ Route to your new page
          },

          {
            path: 'leads/:leadId',
            element: <LeadDetailPage />, 
          },
          {
            path: 'tasks',
            element: <TaskBoardPage />,
          },
          {
            path: 'dashboard/settings/account',
            element: <AccountPage />,
          },
          {
            path: 'app1/tasklogger',
            element: <TaskLoggerPage />,
          },
          {
            path: 'ad-builder',
            element: <AdNameBuilderPage />,
          },
          {
            path: 'facebook/campaign-builder',
            element: <CampaignBuilderPage />,
          },
          {
            path: 'facebook/budget-optimizer',
            element: <BudgetOptimizerPage />,
          },
          {
            path: 'flows',
            element: <FlowHubPage />,
          },
          {
            path: 'flows/zip-code-updates',
            element: <ZipCodeUpdatesFlowPage />,
          },
          {
            path: 'client-inbox',
            element: <ClientInboxPage />,
          },
          {
            path: 'notifications',
            element: (
              <StaffOnly>
                <NotificationsPage />
              </StaffOnly>
            ),
          },
          {
            path: 'knowledge-base',
            element: <KnowledgeBasePage />,
          },
          {
            path: 'creative-studio',
            element: (
              <StaffOnly>
                <CreativeStudioPage />
              </StaffOnly>
            ),
          },
          {
            path: 'client-backend-summary',
            element: <BackendDataSummaryPage />,
          },
          {
            path: 'email-mappings',
            element: <EmailMappingBuilderPage />,
          },

          
          
        ],
      },
      {
        path: 'invoices/:number',
        element: <PublicInvoicePage />,
      },
      {
        path: 'onboarding/:clientSlug?',
        element: <Protect.Authenticated><OnboardingFlowPage /></Protect.Authenticated>,
      },
      {
        path: 'projections/:clientSlug?',
        element: <Protect.Authenticated><PublicProjectionsPage /></Protect.Authenticated>,
      },
      {
        path: '404',
        element: <Protect.Any><Page404 /></Protect.Any>,
      },
      {
        path: '500',
        element: <Protect.Any><Page500 /></Protect.Any>,
      },
      {
        path: '*',
        element: <Protect.Any><Page404 /></Protect.Any>,
      },
    ],
  },

]);

export default router;
